import apiService from './ApiService';
import { GetObservationsApi, AxiosResponse, SaveObservationApi, GetObservationByIdApi, UpdateObservationApi, DeleteObservationApi, TaskDetailsApi, FetchApiWeatherDataApi, SaveObservationLogApi, StoreLocationAddressApi } from '../utils/urlConst';
import dropdownApiService from './DropdownApiService';
import { Observation, WeatherApiData } from '../interfaces/Observation';
import offlineStorageService from '../services/OfflineStorageService';
import { storageKeys } from '../utils/constants';
import exifr from 'exifr';
import LocalStorageUtil from '../utils/localStorageUtil';
import { normalizeObservationValues } from '../components/Observation/ObservationForm';
import { t } from 'i18next';

export interface FaunaSpecies {
    id: string;
    name: string;
    icon: string;
    subcategory?: string; // For "Others" category selections
    groups?: { species_group_id: number; name: string }[];
    species_group_id?: number;
    species_subgroup_id?: number;
}

export interface SpeciesGroupsResponse extends AxiosResponse {
    data: FaunaSpecies[];
}

export interface ObservationsResponse extends AxiosResponse {
    data: Observation[];
}

export interface SaveObservationsParams {
    observationData: Record<string, string | number | boolean | null | undefined>;
    photos?: File[];
    media?: File[];
    mediaTypes?: string[];
    projectId: string | number;
    taskId: string | number;
}

// Photo interfaces for update operations
export interface ExistingPhoto {
    photo_id: number;
    url: string;
    file_path: string;
    file_name?: string;
    obs_species_id: number;
    project_id: number;
    direction?: number;
    taken_at?: string;
    isExisting: true;
    toDelete?: boolean;
}

export interface NewPhoto {
    file: File;
    gpsCoords?: {
        lat: number;
        lng: number;
        direction?: number;
        takenAt?: string;
        source?: string;
        accuracy?: number;
    };
    isExisting: false;
}

export type PhotoItem = ExistingPhoto | NewPhoto;

// Media interfaces for update operations
export interface ExistingMedia {
    media_id: number;
    file_path: string;
    file_name?: string;
    obs_species_id: number;
    type: 'audio' | 'video';
    taken_at?: string;
    isExisting: true;
    toDelete?: boolean;
}

export interface NewMedia {
    file: File;
    type: 'audio' | 'video';
    isExisting: false;
    thumbnail?: File;
}

export type MediaItem = ExistingMedia | NewMedia;
class ObservationApiService {
    private async getPortalProjectId(): Promise<number | null> {
        const value = LocalStorageUtil.getItem(storageKeys.selectedProjectIdForNavigation, null);
        if (value === null || value === undefined || isNaN(Number(value))) return null;
        return Number(value);
        // return LocalStorageUtil.getItem(storageKeys.selectedProjectIdForNavigation, null);
    }
    /**
     * Get the GeoServer workspace from env or default to 'fv'
     */
    private getWorkspace(): string {
        return import.meta.env.VITE_APP_GEOSERVER_WORKSPACE || 'fv';
    }

    /**
     * Get the typename for observations
     */
    private getTypename(): string {
        return 'obs_species';
    }

    /**
     * Get the proxy URL from env or default to localhost
     */
    private getProxy(): string {
        return import.meta.env.VITE_APP_PROXY_URL;
    }

    /**
     * Fetch species groups from the API using DropdownApiService
     * @returns Promise<FaunaSpecies[] | null>
     */
    public async getSpeciesGroups(): Promise<FaunaSpecies[] | null> {

        // If offline, use fallback immediately
        if (!navigator.onLine) {
            return dropdownApiService.getFallbackFaunaSpeciesGroups();
        }

        try {
            const faunaSpecies = await dropdownApiService.getFaunaSpeciesGroups();

            if (faunaSpecies && faunaSpecies.length > 0) {
                return faunaSpecies;
            }

            const fallback = dropdownApiService.getFallbackFaunaSpeciesGroups();
            return fallback;
        } catch (error) {
            console.error('Error fetching species groups:', error);
            const fallback = dropdownApiService.getFallbackFaunaSpeciesGroups();
            return fallback;
        }
    }

    /**
     * Get fallback species data (for offline or error scenarios)
     * @returns FaunaSpecies[]
     */
    public getFallbackSpeciesGroups(): FaunaSpecies[] {
        return [
            {
                id: 'birds',
                name: 'Birds',
                icon: 'birds',
                species_group_id: 10,
            },
            {
                id: 'bats',
                name: 'Bats',
                icon: 'bats',
                species_group_id: 11,
                species_subgroup_id: 30,
            },
            {
                id: 'mammals',
                name: 'Mammals',
                icon: 'mammals',
                species_group_id: 11,
            },
            {
                id: 'others',
                name: 'Others',
                icon: 'others',
                groups: [
                    { species_group_id: 1, name: 'Amphibiërs' },
                    { species_group_id: 2, name: 'Reptielen' },
                    { species_group_id: 3, name: 'Vissen' },
                    { species_group_id: 4, name: 'Insecten' },
                    { species_group_id: 5, name: 'Spinnen' },
                    { species_group_id: 6, name: 'Weekdieren' },
                    { species_group_id: 7, name: 'Kreeftachtigen' },
                    { species_group_id: 8, name: 'Planten' },
                    { species_group_id: 9, name: 'Overig' }
                ],
            },
        ];
    }

    /**
     * Save a new observation using WFS-T via proxy/GeoServer
     * @param {object} observationData - Observation fields (key-value pairs)
     * @param {object} geometry - { lng, lat } in EPSG:4326 (will be converted to EPSG:3857)
     * @param {string|number} projectId - Project ID
     * @param {string|number} taskId - Task ID
     * @param {string|number} layerId - Layer ID
     * @param {object} [auth] - Optional auth headers { authUsername, authPassword, customerId }
     * @returns {Promise<any>} - WFS-T response
     */
    public async saveObservationWFS(
        observationData: Record<string, any>,
        geometry: { lng: number, lat: number },
        projectId: string | number,
        taskId: string | number,
        layerId?: string | number,
        photos: any[] = [], // Changed to accept image items with GPS data
        media: MediaItem[] = [], // Updated to accept MediaItem[] for consistency
        mediaTypes: string[] = [], // Keep for backward compatibility
        auth?: { authUsername?: string, authPassword?: string, authToken?: string, customerId?: string }
    ): Promise<any> {
        // Convert 0 values to null for required fields
        observationData = normalizeObservationValues(observationData);
        if (!observationData.created_at) {
            observationData.created_at = new Date().toISOString();
        }
        // Offline save logic
        const isOffline = !navigator.onLine;
        let isFlexRole = Number(LocalStorageUtil.getItem(storageKeys.isFlexRole, 0));
        observationData.is_flexible = isFlexRole;
        if (isOffline) {
            try {
                // Extract new photos and media BEFORE converting to files
                const newPhotoItems = photos.filter((photoItem): photoItem is NewPhoto => photoItem && !photoItem.isExisting);
                const newMediaItems = media.filter((mediaItem): mediaItem is NewMedia => mediaItem && !mediaItem.isExisting);

                // Extract thumbnails for videos (null for audio or videos without thumbnails)
                const mediaThumbnails = newMediaItems.map(mediaItem => {
                    if (mediaItem.type === 'video' && mediaItem.thumbnail) {
                        return mediaItem.thumbnail; // File object for video thumbnail
                    }
                    return null; // No thumbnail for audio or HEVC videos
                });

                const offlineData = {
                    type: 'create' as const,
                    observationData,
                    geometry,
                    projectId,
                    taskId,
                    layerId,
                    auth,
                    photos: newPhotoItems.map(photoItem => photoItem.file),
                    media: newMediaItems.map(mediaItem => mediaItem.file),
                    mediaTypes: newMediaItems.map(mediaItem => mediaItem.type),
                    mediaThumbnails, // Save thumbnails for offline sync
                };
                await offlineStorageService.saveOfflineObservation(offlineData);
                return {
                    status: true,
                    status_code: 1,
                    status_message: (t("observation.saveOffline"), 'success'),
                    offline: true,
                };
            } catch (offlineError) {
                console.error('Error saving observation offline:', offlineError);
                throw new Error(t("observation.errorSave"));
            }
        }
        console.log("Observation Data in WFS", observationData);

        const workspace = this.getWorkspace();
        const typename = this.getTypename();
        const proxy = this.getProxy();

        // Build WFS-T URL based on your proxy structure (use simpler routes that exist)
        let wfsUrl: string;
        if (layerId) {
            wfsUrl = `${proxy}/${workspace}/wfs/project/${projectId}/layer/${layerId}`;
        } else {
            wfsUrl = `${proxy}/${workspace}/wfs/project/${projectId}`;
        }

        // Transform coordinates from EPSG:4326 (lat/lng) to EPSG:3857 (Web Mercator)
        const x = geometry.lng * 20037508.34 / 180;
        const y = Math.log(Math.tan((90 + geometry.lat) * Math.PI / 360)) / (Math.PI / 180);
        const mercatorY = y * 20037508.34 / 180;

        // Build GML Point geometry following the exact format from your examples
        const gmlGeom = `<gml:Point srsName="http://www.opengis.net/gml/srs/epsg.xml#3857"><gml:coordinates decimal="." cs="," ts=" ">${x},${mercatorY}</gml:coordinates></gml:Point>`;

        // Build XML properties - only include non-null/non-undefined values
        let propertiesXml = `<${workspace}:geom>${gmlGeom}</${workspace}:geom>\n`;

        // Define all possible fields from the GML examples
        const fieldMap = [
            'species_id', 'layer_id', 'company_id', 'date', 'time', 'created_at', 'observer', 'mapnr',
            'not_sure', 'quantity', 'count_method_id', 'ndff_stage_id', 'behaviour_id',
            'trace_id', 'residence_id', 'residence_loc_id', 'remark', 'ndff_gender_id',
            'ndff_determined', 'ndff_counted', 'ndff_cod_id', 'project_id', 'task_id',
            'species_group_id', 'species_subgroup_id', 'is_flexible', 'api_temperature',
            'api_weather_code', 'api_wind_direction', 'api_wind_speed', 'manual_temperature',
            'manual_weather_code', 'manual_wind_direction', 'manual_wind_speed'
        ];

        fieldMap.forEach((key) => {
            const value = observationData[key];

            // Skip null, undefined, and empty string values
            if (value !== undefined && value !== null && value !== '') {
                if (typeof value === 'boolean') {
                    propertiesXml += `<${workspace}:${key}>${value ? 'true' : 'false'}</${workspace}:${key}>\n`;
                } else {
                    // Escape XML special characters
                    const escapedValue = String(value)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                    propertiesXml += `<${workspace}:${key}>${escapedValue}</${workspace}:${key}>\n`;
                }
            }
        });

        // Build WFS-T Insert XML following your namespace structure
        const namespaceUri = import.meta.env.VITE_APP_GEOSERVER_URL || "http://127.0.0.1:8081/fv";
        const xml = `<?xml version="1.0" encoding="UTF-8"?>
        <wfs:Transaction service="WFS" version="1.0.0"
            xmlns:wfs="http://www.opengis.net/wfs"
            xmlns:gml="http://www.opengis.net/gml"
            xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
            xmlns:${workspace}="${namespaceUri}">
            <wfs:Insert>
                <${workspace}:${typename}>
                    ${propertiesXml}
                </${workspace}:${typename}>
            </wfs:Insert>
        </wfs:Transaction>`;

        // Prepare headers
        const headers: Record<string, string> = {
            'Content-Type': 'text/xml; charset=utf-8'
        };

        if (auth?.authUsername) headers['X-Proxy-Username'] = auth.authUsername.replace(/^["']|["']$/g, '');
        if (auth?.authToken) headers['X-Proxy-Token'] = auth.authToken.replace(/^['"]|['"]$/g, '');
        if (auth?.customerId) headers['X-Proxy-Customer-Id'] = auth.customerId;

        try {
            const requestUrl = `${wfsUrl}?service=WFS&version=1.0.0&request=Transaction`;

            // WFS-T POST first
            const response = await fetch(requestUrl, {
                method: 'POST',
                headers,
                body: xml
            });

            const responseText = await response.text();

            // Only on success, upload photos to Laravel
            if (!response.ok) {
                throw new Error(t("observation.errorSave"));
            }

            // Parse the WFS-T response for success indicators
            let wfsResult = null;
            let wfsSuccess = false;
            if (responseText.includes('wfs:WFS_TransactionResponse')) {
                wfsSuccess = /<wfs:SUCCESS\s*\/>/i.test(responseText);
            }

            // Check for error responses
            if (responseText.includes('ows:Exception') || responseText.includes('ServiceException')) {
                const errorMatch = responseText.match(/<ows:ExceptionText>(.*?)<\/ows:ExceptionText>/);
                const errorText = errorMatch ? errorMatch[1] : 'Unknown WFS-T service error';
                throw new Error(`WFS-T service error: ${errorText}`);
            }

            if (!wfsResult) {
                wfsResult = {
                    status: true,
                    status_code: 1,
                    status_message: t("observation.saveSuccess"),
                    wfs_response: responseText,
                    coordinates_transformed: { x, y: mercatorY }
                };
            }

            // Only if WFS-T was successful, proceed to upload photos to Laravel
            let obsSpeciesId: string | undefined = undefined;
            if (wfsSuccess && wfsResult && wfsResult.wfs_response) {
                // Try different possible feature ID formats
                let match = wfsResult.wfs_response.match(/<ogc:FeatureId fid="obs_species\.([0-9]+)"/);
                if (!match) {
                    match = wfsResult.wfs_response.match(/fid="obs_species\.([0-9]+)"/);
                }
                if (match && match[1]) {
                    obsSpeciesId = match[1];
                } else {
                    console.warn('Could not extract observation species id from response');
                }
            }

            let fileUploadResult: any = undefined;
            let fileUploadError: any = undefined;
            let mediaErrors: Record<string, string> = {};
            let photoWFSResults: any[] = [];
            let uploadedPhotos: any[] = [];

            // Upload files to API (if there are files to upload)
            if (wfsSuccess && obsSpeciesId && ((photos && photos.length > 0) || (media && media.length > 0))) {
                try {
                    // Create FormData for all file uploads
                    const allFilesFormData = new FormData();

                    // Add photos (Laravel will store files and return URLs)
                    if (photos && photos.length > 0) {
                        photos.forEach((photoItem) => {
                            allFilesFormData.append('photos[]', photoItem.file);
                        });
                    }

                    // Handle both MediaItem[] and legacy File[] formats
                    const mediaFiles = media.filter((mediaItem): mediaItem is NewMedia => mediaItem && !mediaItem.isExisting);
                    if (mediaFiles && mediaFiles.length > 0) {
                        mediaFiles.forEach((mediaItem, idx) => {
                            allFilesFormData.append('media[]', mediaItem.file);
                            allFilesFormData.append('media_type[]', mediaItem.type);
                            allFilesFormData.append('original_file_name[]', mediaItem.file.name || '');
                            // For ALL videos, send thumbnail entry to maintain array alignment
                            if (mediaItem.type === 'video') {
                                const thumbnail = (mediaItem as any).thumbnail;
                                if (thumbnail) {
                                    // Regular video with thumbnail (MP4, MOV, etc.)
                                    allFilesFormData.append('thumbnail[]', thumbnail);
                                } else {
                                    // HEVC video - send empty Blob with 'null' filename as placeholder
                                    allFilesFormData.append('thumbnail[]', new Blob(), 'null');
                                }
                            }
                        });
                    }

                    allFilesFormData.append('obs_species_id', String(obsSpeciesId));
                    allFilesFormData.append('project_id', String(projectId));
                    if (observationData.tenant_id) {
                        allFilesFormData.append('tenant_id', String(observationData.tenant_id));
                    }
                    const portalProjectId = await this.getPortalProjectId();
                    allFilesFormData.append('portalProjectId', portalProjectId !== null ? String(portalProjectId) : '');
                    console.log('portalProjectId for create:', portalProjectId);

                    // Upload to API first to get file URLs
                    const uploadResponse = await apiService.post(`${SaveObservationApi}?obsSpeciesId=${obsSpeciesId}`, allFilesFormData);

                    if (uploadResponse && typeof uploadResponse === 'object') {
                        const status = (uploadResponse as any).status ?? (uploadResponse as any).data?.status;
                        const statusMessage = (uploadResponse as any).status_message ?? (uploadResponse as any).data?.status_message;
                        if (status === false) {
                            // API returned error, do not proceed
                            throw new Error(statusMessage || t("observation.mediaUploadFailed"));
                        }
                        if ((uploadResponse as any).data) {
                            if ((uploadResponse as any).data.photos) {
                                uploadedPhotos = (uploadResponse as any).data.photos;
                            }
                            if ((uploadResponse as any).data.media) {
                                fileUploadResult = uploadResponse; // Media completely handled
                            }
                        }
                    } else {
                        // Upload failed (server returned error or no response)
                        throw new Error(t("observation.mediaUploadFailed"));
                    }
                } catch (uploadError) {
                    console.error('Failed to upload files:', uploadError);
                    // Throw error to prevent observation from being saved
                    throw new Error(t("observation.mediaUploadFailed") + (uploadError instanceof Error ? (": " + uploadError.message) : ""));
                }
            }

            // Create photo records via WFS-T using the uploaded file URLs
            if (wfsSuccess && obsSpeciesId && uploadedPhotos.length > 0) {
                try {
                    for (let i = 0; i < uploadedPhotos.length; i++) {
                        const uploadedPhoto = uploadedPhotos[i];
                        const originalPhotoItem = photos[i]; // Match with original photo item for GPS data

                        try {
                            let coordinates = null;
                            let direction = null;
                            let takenAt = null;

                            // Use pre-extracted GPS coordinates, direction, and timestamp from UI
                            if (originalPhotoItem.gpsCoords) {
                                coordinates = {
                                    lng: originalPhotoItem.gpsCoords.lng,
                                    lat: originalPhotoItem.gpsCoords.lat
                                };
                                // Extract direction if available in pre-extracted data
                                if (originalPhotoItem.gpsCoords.direction !== undefined && originalPhotoItem.gpsCoords.direction !== null) {
                                    direction = originalPhotoItem.gpsCoords.direction;
                                }

                                // Extract timestamp if available in pre-extracted data
                                if (originalPhotoItem.gpsCoords.takenAt) {
                                    takenAt = originalPhotoItem.gpsCoords.takenAt;
                                }
                            }

                            // Fallback EXIF extraction only if no pre-extracted data available
                            if (!coordinates || direction === null || !takenAt) {
                                try {
                                    const exifData = await exifr.parse(originalPhotoItem.file, ['GPS', 'DateTime', 'DateTimeOriginal', 'CreateDate', 'Orientation']);

                                    // Only extract GPS from EXIF if not already available from UI
                                    if (!coordinates && exifData && exifData.latitude && exifData.longitude) {
                                        coordinates = {
                                            lng: exifData.longitude,
                                            lat: exifData.latitude
                                        };
                                    }

                                    // Extract direction if not already available
                                    if (direction === null && exifData) {
                                        if (exifData.GPSImgDirection !== undefined) {
                                            direction = exifData.GPSImgDirection;
                                        } else if (exifData.GPSDestBearing !== undefined) {
                                            direction = exifData.GPSDestBearing;
                                        } else if (exifData.GPSTrack !== undefined) {
                                            direction = exifData.GPSTrack;
                                        }
                                    }

                                    // Extract timestamp if not already available
                                    if (!takenAt && exifData && (exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime)) {
                                        const timestamp = exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime;
                                        takenAt = new Date(timestamp).toISOString();
                                    }
                                } catch (exifError) {
                                    // Continue without EXIF data
                                }
                            }

                            // Build XML for WFS-T insert into photos table using Laravel-uploaded file URL
                            let photoPropertiesXml = `<${workspace}:obs_species_id>${obsSpeciesId}</${workspace}:obs_species_id>\n`;
                            photoPropertiesXml += `<${workspace}:project_id>${projectId}</${workspace}:project_id>\n`;
                            if (observationData.tenant_id) {
                                photoPropertiesXml += `<${workspace}:tenant_id>${observationData.tenant_id}</${workspace}:tenant_id>\n`;
                            }
                            photoPropertiesXml += `<${workspace}:file_path>${uploadedPhoto.file_path}</${workspace}:file_path>\n`;

                            // Add original file name with XML escaping
                            const originalFileName = originalPhotoItem.file.name;
                            const escapedFileName = originalFileName
                                .replace(/&/g, '&amp;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;')
                                .replace(/"/g, '&quot;')
                                .replace(/'/g, '&apos;');
                            photoPropertiesXml += `<${workspace}:original_file_name>${escapedFileName}</${workspace}:original_file_name>\n`;

                            // Add URL from API response
                            if (uploadedPhoto.url) {
                                photoPropertiesXml += `<${workspace}:url>${uploadedPhoto.url}</${workspace}:url>\n`;
                            }

                            if (uploadedPhoto.base64_image) {
                                photoPropertiesXml += `<${workspace}:base64_image>${uploadedPhoto.base64_image}</${workspace}:base64_image>\n`;
                            }

                            if (observationData.species_group_id) {
                                photoPropertiesXml += `<${workspace}:species_group_id>${observationData.species_group_id}</${workspace}:species_group_id>\n`;
                            }

                            if (observationData.species_subgroup_id) {
                                photoPropertiesXml += `<${workspace}:species_subgroup_id>${observationData.species_subgroup_id}</${workspace}:species_subgroup_id>\n`;
                            }

                            if (direction !== null) {
                                photoPropertiesXml += `<${workspace}:direction>${direction}</${workspace}:direction>\n`;
                            }

                            if (takenAt) {
                                photoPropertiesXml += `<${workspace}:taken_at>${takenAt}</${workspace}:taken_at>\n`;
                            }

                            // Add geometry if GPS coordinates are available and valid in GML format
                            if (
                                coordinates &&
                                typeof coordinates.lat === 'number' &&
                                typeof coordinates.lng === 'number' &&
                                isFinite(coordinates.lat) &&
                                isFinite(coordinates.lng)
                            ) {
                                // Transform coordinates from EPSG:4326 to EPSG:3857 (Web Mercator)
                                const photoX = coordinates.lng * 20037508.34 / 180;
                                const photoY = Math.log(Math.tan((90 + coordinates.lat) * Math.PI / 360)) / (Math.PI / 180);
                                const photoMercatorY = photoY * 20037508.34 / 180;
                                // Create GML Point geometry (same format as observation geometry)
                                const gmlGeom = `<gml:Point srsName="http://www.opengis.net/gml/srs/epsg.xml#3857"><gml:coordinates decimal="." cs="," ts=" ">${photoX},${photoMercatorY}</gml:coordinates></gml:Point>`;
                                photoPropertiesXml += `<${workspace}:geom>${gmlGeom}</${workspace}:geom>\n`;
                            } else {
                                console.log('No valid GPS coordinates for photo save:', originalPhotoItem.file?.name || 'unknown');
                            }

                            // Build WFS-T Insert XML for photos
                            const photoXml = `<?xml version="1.0" encoding="UTF-8"?>
                                <wfs:Transaction service="WFS" version="1.0.0"
                                    xmlns:wfs="http://www.opengis.net/wfs"
                                    xmlns:gml="http://www.opengis.net/gml"
                                    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                                    xmlns:${workspace}="${namespaceUri}">
                                    <wfs:Insert>
                                        <${workspace}:photos>
                                            ${photoPropertiesXml}                
                                        </${workspace}:photos>
                                    </wfs:Insert>
                                </wfs:Transaction>`;

                            // Send WFS-T request for photo
                            const photoResponse = await fetch(`${wfsUrl}?service=WFS&version=1.0.0&request=Transaction`, {
                                method: 'POST',
                                headers,
                                body: photoXml
                            });

                            const photoResponseText = await photoResponse.text();
                            if (!photoResponse.ok) {
                                throw new Error(`Photo WFS-T HTTP error: ${photoResponse.status} ${photoResponse.statusText}\nResponse: ${photoResponseText}`);
                            }

                            // Parse response for success - check multiple success indicators
                            const isSuccessful = photoResponseText.includes('wfs:WFS_TransactionResponse') &&
                                /<wfs:SUCCESS\s*\/>/i.test(photoResponseText);

                            if (isSuccessful) {
                                // Extract Feature ID from response if available
                                let photoId = null;
                                const featureIdMatch = photoResponseText.match(/<ogc:FeatureId fid="photos\.([0-9]+)"/);
                                if (featureIdMatch && featureIdMatch[1]) {
                                    photoId = featureIdMatch[1];
                                }

                                photoWFSResults.push({
                                    photo: originalPhotoItem.file.name,
                                    photoId: photoId,
                                    status: 'success',
                                    coordinates: coordinates,
                                    direction: direction,
                                    takenAt: takenAt,
                                    file_path: uploadedPhoto.file_path,
                                    original_file_name: escapedFileName,
                                    base64_image: uploadedPhoto.base64_image,
                                    response: photoResponseText
                                });
                            } else {
                                // Check for specific error messages - multiple possible formats
                                let errorMessage = 'Insert failed';

                                // Check for WFS-T specific error messages
                                if (photoResponseText.includes('<wfs:Message>')) {
                                    const messageMatch = photoResponseText.match(/<wfs:Message>([\s\S]*?)<\/wfs:Message>/);
                                    errorMessage = messageMatch ? messageMatch[1].trim() : errorMessage;
                                } else if (photoResponseText.includes('<ows:ExceptionText>')) {
                                    const errorMatch = photoResponseText.match(/<ows:ExceptionText>(.*?)<\/ows:ExceptionText>/);
                                    errorMessage = errorMatch ? errorMatch[1] : errorMessage;
                                } else if (photoResponseText.includes('ServiceException')) {
                                    const serviceErrorMatch = photoResponseText.match(/<ServiceException[^>]*>([\s\S]*?)<\/ServiceException>/);
                                    errorMessage = serviceErrorMatch ? serviceErrorMatch[1].trim() : errorMessage;
                                }

                                // Check for FAILED status
                                const isFailedTransaction = photoResponseText.includes('<wfs:FAILED/>') || photoResponseText.includes('<wfs:Status><wfs:FAILED/></wfs:Status>');

                                photoWFSResults.push({
                                    photo: originalPhotoItem.file.name,
                                    status: 'failed',
                                    error: errorMessage,
                                    response: photoResponseText
                                });
                            }

                        } catch (photoError: any) {
                            console.error('Error saving photo:', originalPhotoItem.file.name, photoError);
                            photoWFSResults.push({
                                photo: originalPhotoItem.file.name,
                                status: 'error',
                                error: photoError?.message || 'Unknown error'
                            });
                        }
                    }
                } catch (photoWFSError) {
                    console.error('Error in photo creation phase:', photoWFSError);
                    fileUploadError = photoWFSError;
                }
            }

            // Save observation log after successful create
            if (wfsSuccess && obsSpeciesId) {
                try {
                    await this.saveObservationLog(
                        Number(obsSpeciesId),
                        1 // action_type_id: 1 for create
                    );
                } catch (logError) {
                    console.error('Error saving observation log:', logError);
                    // Don't throw - log errors shouldn't break the main flow
                }
            }

            return {
                ...wfsResult,
                obs_species_id: obsSpeciesId,
                ...(photoWFSResults.length > 0 ? { photo_wfs_results: photoWFSResults } : {}),
                ...(fileUploadResult ? { file_upload: fileUploadResult } : {}),
                ...(fileUploadError ? { file_upload_error: fileUploadError } : {}),
                ...(Object.keys(mediaErrors).length > 0 ? { media_errors: mediaErrors } : {})
            };

        } catch (error: any) {
            console.error('WFS-T save error:', error);
            throw new Error(t("observation.errorSave") + (error?.message ? (": " + error.message) : ""));
        }
    }

    /**
     * Update an existing observation using WFS-T with advanced photo management
     * 
     * ADVANCED PHOTO UPDATE FEATURES:
     * ✅ Smart Photo Detection: Distinguishes between existing and new photos using PhotoItem union types
     * ✅ Selective Processing: Only processes new photos through Laravel+WFS-T workflow  
     * ✅ Photo Deletion: Supports deletion of existing photos via photo_id identification
     * ✅ Append Mode: Adds new photos while preserving existing ones (unless marked for deletion)
     * ✅ Type Safety: Full TypeScript support with ExistingPhoto and NewPhoto interfaces
     * ✅ GPS Preservation: Maintains GPS coordinates and metadata for all photo operations
     * ✅ Comprehensive Analytics: Detailed photo management statistics in response
     * 
     * WORKFLOW:
     * 1. Analyze photos array to separate existing vs new photos
     * 2. Upload new photo files via Laravel API with deletion requests and append mode
     * 3. Create WFS-T database records only for new photos with geometry
     * 4. Return comprehensive results with detailed photo management analytics
     * 
     * @param {string|number} obsSpeciesId - Observation species ID to update
     * @param {object} observationData - Observation fields (key-value pairs)
     * @param {object} geometry - { lng, lat } in EPSG:4326 (will be converted to EPSG:3857)
     * @param {string|number} projectId - Project ID
     * @param {string|number} taskId - Task ID
     * @param {string|number} layerId - Layer ID
     * @param {PhotoItem[]} photos - Array of PhotoItem (ExistingPhoto | NewPhoto) with smart detection
     * @param {File[]} media - Array of media files (handled completely by Laravel)
     * @param {string[]} mediaTypes - Array of media types
     * @param {object} [auth] - Optional auth headers { authUsername, authPassword, customerId }
     * @returns {Promise<any>} - WFS-T response with photo_management analytics
     */
    public async updateObservationWFS(
        obsSpeciesId: string | number,
        observationData: Record<string, any>,
        geometry?: { lng: number, lat: number },
        projectId?: string | number,
        taskId?: string | number,
        layerId?: string | number,
        photos: PhotoItem[] = [], // Updated to use PhotoItem type
        media: MediaItem[] = [], // Updated to use MediaItem type
        mediaTypes: string[] = [], // Keep for backward compatibility
        auth?: { authUsername?: string, authPassword?: string, authToken?: string, customerId?: string }
    ): Promise<any> {
        observationData = normalizeObservationValues(observationData);

        // Offline save logic
        const isOffline = !navigator.onLine;
        if (isOffline) {
            try {
                // Extract media types from NewMedia items BEFORE converting to files
                const newMediaItems = media.filter((mediaItem): mediaItem is NewMedia => mediaItem && !mediaItem.isExisting);

                // Extract thumbnails for videos (null for audio or videos without thumbnails)
                const mediaThumbnails = newMediaItems.map(mediaItem => {
                    if (mediaItem.type === 'video' && mediaItem.thumbnail) {
                        return mediaItem.thumbnail; // File object for video thumbnail
                    }
                    return null; // No thumbnail for audio or HEVC videos
                });

                // Extract IDs of photos and media to delete
                const existingPhotos = photos.filter((photo): photo is ExistingPhoto => photo.isExisting);
                const photosToDelete = existingPhotos.filter(photo => photo.toDelete).map(photo => photo.photo_id);

                const validMedia = media.filter((mediaItem): mediaItem is MediaItem => mediaItem !== null && mediaItem !== undefined);
                const existingMedia = validMedia.filter((mediaItem): mediaItem is ExistingMedia => mediaItem.isExisting);
                const mediaToDelete = existingMedia.filter(mediaItem => mediaItem.toDelete).map(mediaItem => mediaItem.media_id);

                const offlineData = {
                    type: 'update' as const,
                    observationId: obsSpeciesId.toString(),
                    observationData,
                    geometry,
                    projectId: projectId ?? '',
                    taskId: taskId ?? '',
                    layerId: layerId ?? '',
                    auth,
                    photos: photos.filter((photo): photo is NewPhoto => !photo.isExisting).map(photo => photo.file),
                    media: newMediaItems.map(mediaItem => mediaItem.file),
                    mediaTypes: newMediaItems.map(mediaItem => mediaItem.type),
                    mediaThumbnails, // Save thumbnails for offline sync
                    photosToDelete: photosToDelete.length > 0 ? photosToDelete : undefined,
                    mediaToDelete: mediaToDelete.length > 0 ? mediaToDelete : undefined,
                };
                await offlineStorageService.saveOfflineObservation(offlineData);
                return {
                    status: true,
                    status_code: 1,
                    status_message: (t("observation.updateOffline"), 'success'),
                    offline: true,
                };
            } catch (offlineError) {
                console.error('Error saving observation update offline:', offlineError);
                throw new Error(t("observation.errorUpdate"));
            }
        }

        const workspace = this.getWorkspace();
        const typename = this.getTypename();
        const proxy = this.getProxy();

        // Build WFS-T URL based on your proxy structure
        let wfsUrl: string;
        if (layerId) {
            wfsUrl = `${proxy}/${workspace}/wfs/project/${projectId}/layer/${layerId}`;
        } else {
            wfsUrl = `${proxy}/${workspace}/wfs/project/${projectId}`;
        }

        // Build XML properties for update - only include non-null/non-undefined values
        let propertiesXml = '';
        let x: number | undefined;
        let mercatorY: number | undefined;
        if (geometry) {
            x = geometry.lng * 20037508.34 / 180;
            const y = Math.log(Math.tan((90 + geometry.lat) * Math.PI / 360)) / (Math.PI / 180);
            mercatorY = y * 20037508.34 / 180;
            const gmlGeom = `<gml:Point srsName="http://www.opengis.net/gml/srs/epsg.xml#3857"><gml:coordinates decimal="." cs="," ts=" ">${x},${mercatorY}</gml:coordinates></gml:Point>`;
            propertiesXml += `<wfs:Property><wfs:Name>${workspace}:geom</wfs:Name><wfs:Value>${gmlGeom}</wfs:Value></wfs:Property>\n`;
        }

        // Define all possible fields from the GML examples
        const fieldMap = [
            'species_id', 'layer_id', 'company_id', 'date', 'time', 'observer', 'mapnr',
            'not_sure', 'quantity', 'count_method_id', 'ndff_stage_id', 'behaviour_id',
            'trace_id', 'residence_id', 'residence_loc_id', 'remark', 'ndff_gender_id',
            'ndff_determined', 'ndff_counted', 'ndff_cod_id', 'project_id', 'task_id',
            'species_group_id', 'species_subgroup_id', 'api_temperature', 'api_weather_code', 'api_wind_direction', 'api_wind_speed', 'manual_temperature',
            'manual_weather_code', 'manual_wind_direction', 'manual_wind_speed'
        ];

        fieldMap.forEach((key) => {
            // Skip fields not present in observationData at all (not being updated)
            if (!(key in observationData)) return;

            const value = observationData[key];

            if (value !== undefined && value !== null && value !== '') {
                // Field has a value — send it
                let propertyValue: string;
                if (typeof value === 'boolean') {
                    propertyValue = value ? 'true' : 'false';
                } else {
                    // Escape XML special characters
                    propertyValue = String(value)
                        .replace(/&/g, '&amp;')
                        .replace(/</g, '&lt;')
                        .replace(/>/g, '&gt;')
                        .replace(/"/g, '&quot;')
                        .replace(/'/g, '&apos;');
                }
                propertiesXml += `<wfs:Property><wfs:Name>${workspace}:${key}</wfs:Name><wfs:Value>${propertyValue}</wfs:Value></wfs:Property>\n`;
            } else {
                // Field is explicitly null/empty — send empty <wfs:Value/> to clear it in the database
                propertiesXml += `<wfs:Property><wfs:Name>${workspace}:${key}</wfs:Name><wfs:Value/></wfs:Property>\n`;
            }
        });

        // Build WFS-T Update XML with filter for specific obs_species_id
        const namespaceUri = import.meta.env.VITE_APP_GEOSERVER_URL || "http://127.0.0.1:8081/fv";
        const featureFid = `${typename}.${obsSpeciesId}`;
        const xml = `<?xml version="1.0" encoding="UTF-8"?>
        <wfs:Transaction service="WFS" version="1.0.0"
            xmlns:wfs="http://www.opengis.net/wfs"
            xmlns:gml="http://www.opengis.net/gml"
            xmlns:ogc="http://www.opengis.net/ogc"
            xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
            xmlns:${workspace}="${namespaceUri}">
            <wfs:Update typeName="${workspace}:${typename}">
                    ${propertiesXml}                
                    <ogc:Filter>
                    <ogc:FeatureId fid="${featureFid}" />
                </ogc:Filter>
            </wfs:Update>
        </wfs:Transaction>`;

        // Prepare headers
        const headers: Record<string, string> = {
            'Content-Type': 'text/xml; charset=utf-8'
        };

        if (auth?.authUsername) headers['X-Proxy-Username'] = auth.authUsername.replace(/^["']|["']$/g, '');
        if (auth?.authToken) headers['X-Proxy-Token'] = auth.authToken.replace(/^['"]|['"]$/g, '');
        if (auth?.customerId) headers['X-Proxy-Customer-Id'] = auth.customerId;

        try {
            const requestUrl = `${wfsUrl}?service=WFS&version=1.0.0&request=Transaction`;

            const response = await fetch(requestUrl, {
                method: 'POST',
                headers,
                body: xml
            });

            const responseText = await response.text();
            if (!response.ok) {
                throw new Error(t("observation.errorUpdate"));
            }

            let wfsResult = null;
            // WFS-T update success detection
            if (responseText.includes('wfs:WFS_TransactionResponse')) {
                const wfsSuccess = /<wfs:SUCCESS\s*\/>/i.test(responseText);
                const wfsFailed = /<wfs:FAILED\s*\/>/i.test(responseText);
                if (wfsSuccess) {
                    wfsResult = {
                        status: true,
                        status_code: 1,
                        status_message: 'Observation updated successfully',
                        wfs_response: responseText,
                        coordinates_transformed: { x, y: mercatorY }
                    };
                } else if (wfsFailed) {
                    throw new Error('Update failed');
                }
            }

            // Check for error responses
            if (responseText.includes('ows:Exception') || responseText.includes('ServiceException')) {
                const errorMatch = responseText.match(/<ows:ExceptionText>(.*?)<\/ows:ExceptionText>/);
                const errorText = errorMatch ? errorMatch[1] : 'Unknown WFS-T service error';
                throw new Error(`WFS-T service error: ${errorText}`);
            }

            if (!wfsResult) {
                wfsResult = {
                    status: true,
                    status_code: 1,
                    status_message: (t("observation.updateSuccess"), 'success'),
                    wfs_response: responseText,
                    coordinates_transformed: { x, y: mercatorY }
                };
            }

            // Handle file uploads and photo WFS-T updates with smart photo detection
            let fileUploadResult: any = undefined;
            let fileUploadError: any = undefined;
            let mediaErrors: Record<string, string> = {};
            let photoWFSResults: any[] = [];

            // Analyze photos and media, separate existing vs new
            const existingPhotos = photos.filter((photo): photo is ExistingPhoto => photo.isExisting);
            const newPhotos = photos.filter((photo): photo is NewPhoto => !photo.isExisting);
            const photosToDelete = existingPhotos.filter(photo => photo.toDelete);
            const photosToKeep = existingPhotos.filter(photo => !photo.toDelete);

            // Filter out null/undefined media items before processing
            const validMedia = media.filter((mediaItem): mediaItem is MediaItem => mediaItem !== null && mediaItem !== undefined);
            const existingMedia = validMedia.filter((mediaItem): mediaItem is ExistingMedia => mediaItem.isExisting);
            const newMedia = validMedia.filter((mediaItem): mediaItem is NewMedia => !mediaItem.isExisting);
            const mediaToDelete = existingMedia.filter(mediaItem => mediaItem.toDelete);
            const mediaToKeep = existingMedia.filter(mediaItem => !mediaItem.toDelete);

            // Upload new files and handle deletions via Laravel API (get URLs first)
            let uploadedNewPhotos: any[] = [];
            if ((newPhotos && newPhotos.length > 0) || (newMedia && newMedia.length > 0) || photosToDelete.length > 0 || mediaToDelete.length > 0) {
                try {


                    // Create FormData for all file operations
                    const allFilesFormData = new FormData();

                    // Add new photos only (Laravel will store files, WFS-T will handle photo records)
                    if (newPhotos && newPhotos.length > 0) {
                        newPhotos.forEach((photoItem) => {
                            allFilesFormData.append('photos[]', photoItem.file);
                        });
                    }

                    // Add photos to delete (photo IDs)
                    if (photosToDelete.length > 0) {
                        photosToDelete.forEach((photoItem) => {
                            allFilesFormData.append('photos_to_delete[]', photoItem.photo_id.toString());
                        });

                    }

                    // Add media to delete (media IDs)
                    if (mediaToDelete.length > 0) {
                        mediaToDelete.forEach((mediaItem) => {
                            allFilesFormData.append('media_to_delete[]', mediaItem.media_id.toString());
                        });
                    }

                    // Set mode flag for Laravel API
                    allFilesFormData.append('mode', 'append'); // append new files, delete specified ones

                    // Add new media files (Laravel will handle both files AND database records)
                    if (newMedia && newMedia.length > 0) {
                        newMedia.forEach((mediaItem) => {
                            allFilesFormData.append('media[]', mediaItem.file);
                            allFilesFormData.append('media_type[]', mediaItem.type);
                            allFilesFormData.append('original_file_name[]', mediaItem.file.name || '');
                            if (mediaItem.type === 'video') {
                                const thumbnail = (mediaItem as any).thumbnail;
                                if (thumbnail) {
                                    // Regular video with thumbnail (MP4, MOV, etc.)
                                    allFilesFormData.append('thumbnail[]', thumbnail);
                                } else {
                                    // HEVC video - send empty Blob with 'null' filename as placeholder
                                    allFilesFormData.append('thumbnail[]', new Blob(), 'null');
                                }
                            }
                        });
                    }

                    allFilesFormData.append('obs_species_id', String(obsSpeciesId));
                    allFilesFormData.append('project_id', String(projectId));
                    if (observationData.tenant_id) {
                        allFilesFormData.append('tenant_id', String(observationData.tenant_id));
                    }
                    const portalProjectId = await this.getPortalProjectId();
                    allFilesFormData.append('portalProjectId', portalProjectId !== null ? String(portalProjectId) : '');
                    console.log('portalProjectId for update:', portalProjectId);

                    // Upload to Laravel API with update flag (get file URLs)
                    const uploadUrl = `${SaveObservationApi}?obsSpeciesId=${encodeURIComponent(obsSpeciesId)}&update=true`;
                    const uploadResponse = await apiService.post(uploadUrl, allFilesFormData);

                    if (uploadResponse && typeof uploadResponse === 'object') {
                        const status = (uploadResponse as any).status ?? (uploadResponse as any).data?.status;
                        const statusMessage = (uploadResponse as any).status_message ?? (uploadResponse as any).data?.status_message;
                        if (status === false) {
                            // API returned error, do not proceed
                            throw new Error(statusMessage || t("observation.mediaUploadFailed"));
                        }
                        if ((uploadResponse as any).data) {
                            if ((uploadResponse as any).data.photos) {
                                uploadedNewPhotos = (uploadResponse as any).data.photos;
                            }
                            if ((uploadResponse as any).data.media) {
                                fileUploadResult = uploadResponse; // Media completely handled
                            }
                        }
                    } else {
                        // Upload failed (server returned error or no response)
                        throw new Error(t("observation.mediaUploadFailed"));
                    }
                } catch (uploadError) {
                    throw new Error(t("observation.mediaUploadFailed") + (uploadError instanceof Error ? (": " + uploadError.message) : ""));
                }
            }

            // Create photo records via WFS-T for new photos using the uploaded file URLs from Laravel
            if (uploadedNewPhotos.length > 0) {
                try {

                    for (let i = 0; i < uploadedNewPhotos.length; i++) {
                        const uploadedPhoto = uploadedNewPhotos[i];
                        const originalPhotoItem = newPhotos[i]; // Match with new photo item for GPS data

                        try {
                            let coordinates = null;
                            let direction = null;
                            let takenAt = null;

                            // Use pre-extracted GPS coordinates, direction, and timestamp from UI
                            if (originalPhotoItem && originalPhotoItem.gpsCoords) {
                                coordinates = {
                                    lng: originalPhotoItem.gpsCoords.lng,
                                    lat: originalPhotoItem.gpsCoords.lat
                                };

                                // Extract direction if available in pre-extracted data
                                if (originalPhotoItem.gpsCoords.direction !== undefined && originalPhotoItem.gpsCoords.direction !== null) {
                                    direction = originalPhotoItem.gpsCoords.direction;
                                }

                                // Extract timestamp if available in pre-extracted data
                                if (originalPhotoItem.gpsCoords.takenAt) {
                                    takenAt = originalPhotoItem.gpsCoords.takenAt;

                                }
                            }

                            // Fallback EXIF extraction only if no pre-extracted data available
                            if (!coordinates || direction === null || !takenAt) {
                                try {
                                    const photoFile = originalPhotoItem.file;
                                    const exifData = await exifr.parse(photoFile, ['GPS', 'DateTime', 'DateTimeOriginal', 'CreateDate', 'Orientation']);


                                    // Only extract GPS from EXIF if not already available from UI
                                    if (!coordinates && exifData && exifData.latitude && exifData.longitude) {
                                        coordinates = {
                                            lng: exifData.longitude,
                                            lat: exifData.latitude
                                        };

                                    }

                                    // Extract direction if not already available
                                    if (direction === null && exifData) {
                                        if (exifData.GPSImgDirection !== undefined) {
                                            direction = exifData.GPSImgDirection;

                                        } else if (exifData.GPSDestBearing !== undefined) {
                                            direction = exifData.GPSDestBearing;

                                        } else if (exifData.GPSTrack !== undefined) {
                                            direction = exifData.GPSTrack;

                                        }
                                    }

                                    // Extract timestamp if not already available
                                    if (!takenAt && exifData && (exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime)) {
                                        const timestamp = exifData.DateTimeOriginal || exifData.CreateDate || exifData.DateTime;
                                        takenAt = new Date(timestamp).toISOString();

                                    }
                                } catch (exifError) {
                                    console.warn('Failed to extract fallback EXIF data from new photo for update:', originalPhotoItem.file?.name || 'unknown', exifError);
                                    // Continue without EXIF data
                                }
                            }

                            // Build XML for WFS-T insert into photos table using Laravel-uploaded file URL
                            let photoPropertiesXml = `<${workspace}:obs_species_id>${obsSpeciesId}</${workspace}:obs_species_id>\n`;
                            if (projectId) photoPropertiesXml += `<${workspace}:project_id>${projectId}</${workspace}:project_id>\n`;
                            if (observationData.tenant_id) {
                                photoPropertiesXml += `<${workspace}:tenant_id>${observationData.tenant_id}</${workspace}:tenant_id>\n`;
                            }
                            photoPropertiesXml += `<${workspace}:file_path>${uploadedPhoto.file_path}</${workspace}:file_path>\n`;

                            // Add original file name with XML escaping
                            const originalFileName = originalPhotoItem.file.name;
                            const escapedFileName = originalFileName
                                .replace(/&/g, '&amp;')
                                .replace(/</g, '&lt;')
                                .replace(/>/g, '&gt;')
                                .replace(/"/g, '&quot;')
                                .replace(/'/g, '&apos;');
                            photoPropertiesXml += `<${workspace}:original_file_name>${escapedFileName}</${workspace}:original_file_name>\n`;

                            // Add URL from API response
                            if (uploadedPhoto.url) {
                                photoPropertiesXml += `<${workspace}:url>${uploadedPhoto.url}</${workspace}:url>\n`;
                            }

                            if (uploadedPhoto.base64_image) {
                                photoPropertiesXml += `<${workspace}:base64_image>${uploadedPhoto.base64_image}</${workspace}:base64_image>\n`;
                            }

                            // Add species group data if available from observation data
                            if (observationData.species_group_id) {
                                photoPropertiesXml += `<${workspace}:species_group_id>${observationData.species_group_id}</${workspace}:species_group_id>\n`;
                            }

                            if (observationData.species_subgroup_id) {
                                photoPropertiesXml += `<${workspace}:species_subgroup_id>${observationData.species_subgroup_id}</${workspace}:species_subgroup_id>\n`;
                            }

                            if (direction !== null) {
                                photoPropertiesXml += `<${workspace}:direction>${direction}</${workspace}:direction>\n`;
                            }

                            if (takenAt) {
                                photoPropertiesXml += `<${workspace}:taken_at>${takenAt}</${workspace}:taken_at>\n`;
                            }

                            // Add geometry if GPS coordinates are available and valid in GML format
                            if (
                                coordinates &&
                                typeof coordinates.lat === 'number' &&
                                typeof coordinates.lng === 'number' &&
                                isFinite(coordinates.lat) &&
                                isFinite(coordinates.lng)
                            ) {
                                // Transform coordinates from EPSG:4326 to EPSG:3857 (Web Mercator)
                                const photoX = coordinates.lng * 20037508.34 / 180;
                                const photoY = Math.log(Math.tan((90 + coordinates.lat) * Math.PI / 360)) / (Math.PI / 180);
                                const photoMercatorY = photoY * 20037508.34 / 180;
                                // Create GML Point geometry (same format as observation geometry)
                                const gmlGeom = `<gml:Point srsName="http://www.opengis.net/gml/srs/epsg.xml#3857"><gml:coordinates decimal="." cs="," ts=" ">${photoX},${photoMercatorY}</gml:coordinates></gml:Point>`;

                                photoPropertiesXml += `<${workspace}:geom>${gmlGeom}</${workspace}:geom>\n`;
                            } else {
                                console.log('No valid GPS coordinates for photo update:', originalPhotoItem.file?.name || 'unknown');
                            }

                            // Build WFS-T Insert XML for photos (insert new photo record)
                            const photoXml = `<?xml version="1.0" encoding="UTF-8"?>
                                <wfs:Transaction service="WFS" version="1.0.0"
                                    xmlns:wfs="http://www.opengis.net/wfs"
                                    xmlns:gml="http://www.opengis.net/gml"
                                    xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
                                    xmlns:${workspace}="${namespaceUri}">
                                    <wfs:Insert>
                                        <${workspace}:photos>
                                            ${photoPropertiesXml}                
                                        </${workspace}:photos>
                                    </wfs:Insert>
                                </wfs:Transaction>`;



                            // Send WFS-T request for photo
                            const photoResponse = await fetch(`${wfsUrl}?service=WFS&version=1.0.0&request=Transaction`, {
                                method: 'POST',
                                headers,
                                body: photoXml
                            });

                            const photoResponseText = await photoResponse.text();



                            if (!photoResponse.ok) {
                                throw new Error(`Photo WFS-T HTTP error: ${photoResponse.status} ${photoResponse.statusText}\nResponse: ${photoResponseText}`);
                            }

                            // Parse response for success - check multiple success indicators
                            const isSuccessful = photoResponseText.includes('wfs:WFS_TransactionResponse') &&
                                /<wfs:SUCCESS\s*\/>/i.test(photoResponseText);

                            if (isSuccessful) {
                                // Extract Feature ID from response if available
                                let photoId = null;
                                const featureIdMatch = photoResponseText.match(/<ogc:FeatureId fid="photos\.([0-9]+)"/);
                                if (featureIdMatch && featureIdMatch[1]) {
                                    photoId = featureIdMatch[1];
                                }

                                photoWFSResults.push({
                                    photo: originalPhotoItem.file?.name || 'unknown',
                                    photoId: photoId,
                                    status: 'success',
                                    coordinates: coordinates,
                                    direction: direction,
                                    takenAt: takenAt,
                                    url: uploadedPhoto.url,
                                    file_path: uploadedPhoto.file_path,
                                    original_file_name: escapedFileName,
                                    base64_image: uploadedPhoto.base64_image,
                                    response: photoResponseText
                                });
                            } else {
                                // Check for specific error messages - multiple possible formats
                                let errorMessage = 'Insert failed';

                                // Check for WFS-T specific error messages
                                if (photoResponseText.includes('<wfs:Message>')) {
                                    const messageMatch = photoResponseText.match(/<wfs:Message>([\s\S]*?)<\/wfs:Message>/);
                                    errorMessage = messageMatch ? messageMatch[1].trim() : errorMessage;
                                } else if (photoResponseText.includes('<ows:ExceptionText>')) {
                                    const errorMatch = photoResponseText.match(/<ows:ExceptionText>(.*?)<\/ows:ExceptionText>/);
                                    errorMessage = errorMatch ? errorMatch[1] : errorMessage;
                                } else if (photoResponseText.includes('ServiceException')) {
                                    const serviceErrorMatch = photoResponseText.match(/<ServiceException[^>]*>([\s\S]*?)<\/ServiceException>/);
                                    errorMessage = serviceErrorMatch ? serviceErrorMatch[1].trim() : errorMessage;
                                }

                                // Check for FAILED status
                                const isFailedTransaction = photoResponseText.includes('<wfs:FAILED/>') || photoResponseText.includes('<wfs:Status><wfs:FAILED/></wfs:Status>');

                                photoWFSResults.push({
                                    photo: originalPhotoItem.file?.name || 'unknown',
                                    status: 'failed',
                                    error: errorMessage,
                                    response: photoResponseText
                                });
                            }

                        } catch (photoError: any) {
                            console.error('Error saving new photo for update:', originalPhotoItem.file?.name || 'unknown', photoError);
                            photoWFSResults.push({
                                photo: originalPhotoItem.file?.name || 'unknown',
                                status: 'error',
                                error: photoError?.message || 'Unknown error'
                            });
                        }
                    }
                } catch (photoWFSError) {
                    console.error('Error in new photo update phase:', photoWFSError);
                    fileUploadError = photoWFSError;
                }
            }

            // Compile comprehensive results
            const updateResults = {
                ...wfsResult,
                photo_management: {
                    total_photos: photos.length,
                    existing_photos: existingPhotos.length,
                    new_photos: newPhotos.length,
                    photos_to_delete: photosToDelete.length,
                    photos_to_keep: photosToKeep.length,
                    new_photos_uploaded: uploadedNewPhotos.length,
                    new_photos_wfs_processed: photoWFSResults.length
                },
                media_management: {
                    total_media: media.length,
                    existing_media: existingMedia.length,
                    new_media: newMedia.length,
                    media_to_delete: mediaToDelete.length,
                    media_to_keep: mediaToKeep.length
                },
                ...(photoWFSResults.length > 0 ? { photo_wfs_results: photoWFSResults } : {}),
                ...(fileUploadResult ? { file_upload: fileUploadResult } : {}),
                ...(fileUploadError ? { file_upload_error: fileUploadError } : {}),
                ...(Object.keys(mediaErrors).length > 0 ? { media_errors: mediaErrors } : {})
            };

            // Save observation log after successful update
            try {
                await this.saveObservationLog(
                    Number(obsSpeciesId),
                    2 // action_type_id: 2 for update
                );
            } catch (logError) {
                console.error('Error saving observation log:', logError);
                // Don't throw - log errors shouldn't break the main flow
            }

            return updateResults;

        } catch (error: any) {
            console.error('WFS-T update error:', error);
            throw new Error(t("observation.errorUpdate"));
        }
    }



    /**
     * Fetch observations from the API
     * @param taskId - Optional task ID to filter observations
     * @param isFlexUser
     * @returns Promise<Observation[] | null>
     */
    public async getObservations(taskId?: string, isFlexUser?: boolean): Promise<Observation[] | null> {
        try {
            let flexRole: number | null = null;
            if (typeof window !== 'undefined') {
                const flexRoleRaw = localStorage.getItem(storageKeys.isFlexRole);
                flexRole = flexRoleRaw !== null ? Number(flexRoleRaw) : null;
            }
            let isFlex = isFlexUser;
            if (flexRole === 1) {

                isFlex = true;
            }
            const params = taskId ? { taskId, isFlexUser: isFlex } : { isFlexUser: isFlex };
            const response = await apiService.get<ObservationsResponse>(GetObservationsApi, params);

            if (response && response.status === true && response.status_code === 1 && Array.isArray(response.data)) {
                return response.data;
            }
            return null;
        } catch (error) {
            console.error('Error fetching observations:', error);
            return null;
        }
    }

    /**
     * Fetch a single observation by obs_species_id
     * @param obsSpeciesId - Observation species ID
     * @returns {Promise<any>} - API response
     */
    public async getObservationById(obsSpeciesId: string | number): Promise<any> {
        try {
            const url = `${GetObservationByIdApi}?obsSpeciesId=${encodeURIComponent(obsSpeciesId)}`;
            const response = await apiService.get(url);
            return response;
        } catch (error) {
            console.error('Error fetching observation by ID:', error);
            throw error;
        }
    }

    /**
     * Delete an observation by obsId
     * @param obsId - Observation ID
     * @returns {Promise<any>} - API response
     */
    public async deleteObservation(obsId: string | number): Promise<any> {
        try {
            const portalProjectId = await this.getPortalProjectId();
            const userId = Number(LocalStorageUtil.getItem(storageKeys.workerId, "0"));
            const url = `${DeleteObservationApi}?obsId=${encodeURIComponent(obsId)}${portalProjectId !== null ? `&portalProjectId=${encodeURIComponent(String(portalProjectId))}` : ''}&action_via=Fieldapp&user_id=${userId}`;
            const response = await apiService.delete(url);

            // Save observation log after successful delete
            // if (response && (response as any).status) {
            //     try {
            //         await this.saveObservationLog(
            //             Number(obsId),
            //             3 // action_type_id: 3 for delete
            //         );
            //     } catch (logError) {
            //         console.error('Error saving observation log:', logError);
            //         // Don't throw - log errors shouldn't break the main flow
            //     }
            // }

            return response;
        } catch (error: any) {
            console.error('Error deleting observation:', error);
            if (error?.response) {
                console.error('Error status:', error.response.status);
                console.error('Error data:', error.response.data);
                console.error('Error message:', error.response.statusText);
            }
            throw error;
        }
    }

    /**
     * Fetch task details for a given taskId (returns layer_id, project_id, etc)
     * @param {string|number} taskId - Task ID
     * @returns {Promise<any>} - Task details object
     */
    public async getTaskDetails(taskId: string | number): Promise<any> {
        try {
            const params = { task_id: taskId };
            const response = await apiService.get(TaskDetailsApi, params);
            if (response && typeof response === 'object' && 'data' in response && response.data) {
                return response.data;
            }
            return response;
        } catch (error) {
            console.error('Error fetching task details:', error);
            throw error;
        }
    }

    /**
     * Update observation location (geom only)
     * @param {string|number} obsSpeciesId - Observation species ID
     * @param {string} geom - POINT geometry string
     * @returns {Promise<any>} - API response
     */
    public async updateObservationLocation(obsSpeciesId: string | number, geom: string): Promise<any> {
        try {
            // Get task details from localStorage to build proper WFS URL
            const taskId = LocalStorageUtil.getItem(storageKeys.taskId, '');
            const projectData = localStorage.getItem('currentProjectData');
            let projectId: string | number = '';
            let layerId: string | number | undefined = undefined;

            if (projectData) {
                try {
                    const parsed = JSON.parse(projectData);
                    projectId = parsed.project_id || '';
                    layerId = parsed.layer_id;
                } catch (e) {
                    console.warn('Failed to parse project data from localStorage');
                }
            }

            // Get auth credentials
            const authToken = localStorage.getItem('authToken') || '';
            const emailId = (localStorage.getItem('emailId') || '').replace(/^["']|["']$/g, '');
            const customerId = import.meta.env.VITE_APP_COMPANY_ID;

            // Parse the POINT string to extract coordinates
            const coordMatch = geom.match(/POINT\(([^\s]+)\s+([^\)]+)\)/);
            if (!coordMatch) {
                throw new Error('Invalid POINT geometry format');
            }

            const lng = parseFloat(coordMatch[1]);
            const lat = parseFloat(coordMatch[2]);

            // Use the existing updateObservationWFS method with only geometry update
            const result = await this.updateObservationWFS(
                obsSpeciesId,
                {}, // Empty observation data - we only want to update geometry
                { lng, lat }, // Geometry to update
                projectId,
                taskId,
                layerId,
                [], // No photos
                [], // No media
                [], // No media types
                {
                    authUsername: emailId,
                    authToken: authToken,
                    customerId: customerId
                }
            );

            return result;
        } catch (error) {
            console.error('Error updating observation location:', error);
            throw error;
        }
    }

    /**
     * Fetch weather data from backend API
     * @param {string} latitude - Latitude coordinate
     * @param {string} longitude - Longitude coordinate
     * @returns {Promise<WeatherApiData | null>} - Weather data response
     */
    public async fetchWeatherData(latitude: string, longitude: string): Promise<WeatherApiData | null> {
        try {
            const userToken = LocalStorageUtil.getItem(storageKeys.authToken, "");
            const userId = LocalStorageUtil.getItem(storageKeys.userId, "");

            const jsondata = JSON.stringify({
                latitude: latitude,
                longitude: longitude,
            });

            apiService.setAuthToken(userToken);

            const response: WeatherApiData | null = await apiService.get(
                FetchApiWeatherDataApi,
                jsondata
            );

            if (response && response.status && response.status_code === 200) {
                return response;
            }

            return null;
        } catch (error) {
            console.error('Error fetching weather data:', error);
            return null;
        }
    }

    /**
     * Store location address for a saved observation
     * @param {string|number} obsSpeciesId - Observation species ID (from WFS save response)
     * @param {number} lat - Latitude coordinate
     * @param {number} lon - Longitude coordinate
     * @returns {Promise<any>} - API response
     */
    public async storeLocationAddress(
        obsSpeciesId: string | number,
        lat: number,
        lon: number
    ): Promise<any> {
        try {
            const payload = {
                obsSpeciesId: obsSpeciesId,
                lat: lat,
                lon: lon,
                is_from_app: 1,
            };
            const response = await apiService.post(StoreLocationAddressApi, payload);
            return response;
        } catch (error) {
            console.error('Error storing location address:', error);
            // Non-critical – do not break the main flow
            return null;
        }
    }

    /**
     * Save observation log after create/update/delete operations
     * @param {number} observationId - Observation species ID
     * @param {number} actionTypeId - Action type: 1=create, 2=update, 3=delete
     * @param {number} isFlexible - Is flexible user (0 or 1)
     * @param {string} actionVia - Action via source (e.g., "Fieldapp")
     * @param {number} userId - User ID
     * @returns {Promise<any>} - API response
     */
    public async saveObservationLog(
        observationId: number,
        actionTypeId: number,
        isFlexible?: number,
        actionVia: string = "Fieldapp",
        userId?: number
    ): Promise<any> {
        try {
            // Get userId from localStorage if not provided
            const logUserId = userId || Number(LocalStorageUtil.getItem(storageKeys.workerId, "0"));
            // Get isFlexRole from localStorage if not provided
            const logIsFlexible = isFlexible !== undefined ? isFlexible : Number(LocalStorageUtil.getItem(storageKeys.isFlexRole, 0));

            const payload = {
                observation_id: observationId,
                action_type_id: actionTypeId,
                is_flexible: logIsFlexible,
                action_via: actionVia,
                user_id: logUserId
            };

            console.log('Saving observation log:', payload);

            const response = await apiService.post(SaveObservationLogApi, payload);
            return response;
        } catch (error) {
            console.error('Error saving observation log:', error);
            // Don't throw error, just log it - we don't want to break the main flow
            return null;
        }
    }

}

const observationApiService = new ObservationApiService();
export default observationApiService;
